/**
 * Compares a horse's position in early and latest predictions to determine ranking change
 * @param {Object} horse - The horse to check
 * @param {Array} earlyPredictions - Array of early predictions
 * @param {Array} latestPredictions - Array of latest predictions
 * @param {Number} maxVisibleTips - Maximum number of tips being displayed (defaults to 2)
 * @returns {'up' | 'down' | 'same' | 'double-up' | 'double-down' | null} - The ranking change status
 */
import { logger } from "@/utils/logger";

export const getRankingChange = (
  horse,
  earlyPredictions,
  latestPredictions,
  maxVisibleTips = 2,
) => {
  if (!earlyPredictions || !latestPredictions || !horse) return null;

  // Find horse in latest predictions
  let latestRank = latestPredictions.findIndex(
    (h) =>
      h.number === horse.number &&
      h.name?.toLowerCase().trim() === horse.name?.toLowerCase().trim(),
  );

  // Find horse in early predictions
  let earlyRank = earlyPredictions.findIndex(
    (h) =>
      h.formattedName?.includes(horse.number + ".") &&  h.formattedName?.includes(horse.name)||
      (h.number === horse.number &&
        h.name?.toLowerCase().trim() === horse.name?.toLowerCase().trim()),
  );
  // If either rank is not found, set to -1 to indicate not in the list
  if (latestRank === -1) latestRank = Number.MAX_SAFE_INTEGER;
  if (earlyRank === -1) earlyRank = Number.MAX_SAFE_INTEGER;

  // Implementing the requested logic:

  // Case 1: If entry rank stays the same
  if (latestRank === earlyRank) {
    return "same";
  }

  // Convert maxVisibleTips to zero-based index (e.g. if showing 4 tips, last index is 3)
  const maxVisibleIndex = maxVisibleTips - 1;

  // Case 2: If entry rank decreases but still visible in the tips
  if (latestRank > earlyRank && latestRank <= maxVisibleIndex) {
    return "down";
  }

  // Case 3: If entry rank increases and was initially visible in tips
  if (latestRank < earlyRank && earlyRank <= maxVisibleIndex) {
    return "up";
  }

  // Case 4: If entry rank decreases and is no longer visible in tips
  if (latestRank > earlyRank && latestRank > maxVisibleIndex) {
    return "double-down";
  }

  // Case 5: If entry rank increases and was not initially visible in tips
  if (latestRank < earlyRank && earlyRank > maxVisibleIndex) {
    return "double-up";
  }

  // Default case if none of the conditions match
  return null;
};
